import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { trpc, trpcClient } from '@/shared/lib/trpc'
import { db, type LocalSession, type LocalSessionLine, type OfflineQueueItem } from '@/shared/lib/dexie'
import { useNetworkStatus } from '@/shared/lib/network'
import { isNetworkError, isTransportError, isUnauthorizedError, friendlyMutationError } from '@/shared/lib/errors'

interface OfflineContextValue {
  online: boolean
  pendingCount: number
  failedCount: number
  failedItems: OfflineQueueItem[]
  authError: boolean
  retryItem: (id: string) => Promise<void>
  discardItem: (id: string) => Promise<void>
}

const OfflineContext = createContext<OfflineContextValue>({
  online: true,
  pendingCount: 0,
  failedCount: 0,
  failedItems: [],
  authError: false,
  retryItem: async () => {},
  discardItem: async () => {},
})

export function OfflineProvider({ children }: { children: ReactNode }) {
  const { org, session } = useAuth()
  const utils = trpc.useUtils()
  const online = useNetworkStatus()
  const [pendingCount, setPendingCount] = useState(0)
  const [failedCount, setFailedCount] = useState(0)
  const [failedItems, setFailedItems] = useState<OfflineQueueItem[]>([])
  const [authError, setAuthError] = useState(false)
  const [flushTick, setFlushTick] = useState(0)
  const seededOrgId = useRef<string | null>(null)
  const isSyncing = useRef(false)

  async function refreshCounts() {
    const all = await db.offline_queue.toArray()
    setPendingCount(all.filter(i => i.synced_at === null && !i.failed_at).length)
    const failed = all.filter(i => i.failed_at != null)
    setFailedCount(failed.length)
    setFailedItems(failed)
  }

  // Keep Dexie org in sync with current org state (deleted_at changes, etc.)
  useEffect(() => {
    if (org) {
      db.organizations.put(org).catch(() => {})
    }
  }, [org])

  // Seed products + org + history into Dexie once per org (or on org change)
  useEffect(() => {
    if (!org || !session || seededOrgId.current === org.id) return
    seededOrgId.current = org.id
    async function seed() {
      try {
        const products = await trpcClient.products.list.query({ archived: false })
        await db.products.where('workspace_id').equals(org!.id).delete()
        await db.products.bulkPut(products)
        await db.organizations.put(org!)
      } catch {}

      try {
        const historyData = await trpcClient.history.seed.query({})
        // Drop stale local sessions before re-seeding, but keep ones still
        // pending in the offline queue (not yet pushed to the server)
        const queued = await db.offline_queue.toArray()
        const pendingIds = new Set(
          queued
            // Dead items (failed_at) are never flushed — let their local
            // phantom sessions be cleaned up by the reseed below.
            .filter(i => i.synced_at === null && !i.failed_at)
            .map(i => (i.input as { clientId?: string }).clientId)
            .filter((id): id is string => !!id)
        )
        const staleIds = (await db.sessions.where('workspace_id').equals(org!.id).primaryKeys())
          .filter(id => !pendingIds.has(id as string))
        await db.session_lines.where('session_id').anyOf(staleIds).delete()
        await db.sessions.bulkDelete(staleIds)
        await storeHistorySeed(historyData)
        await db.meta.put({ key: `history_synced_at:${org!.id}`, value: new Date().toISOString() })
      } catch {}

      try {
        const debtors = await trpcClient.credit.listDebtorNames.query()
        await db.credit_debtors.where('workspace_id').equals(org!.id).delete()
        await db.credit_debtors.bulkPut(debtors.map(d => ({ ...d, workspace_id: org!.id })))
      } catch {}

      await refreshCounts()
    }
    seed()
  }, [org?.id, session?.user?.id])

  // Flush offline queue + reseed when back online, org changes, or flush tick fires
  useEffect(() => {
    if (!online || !org || !session || isSyncing.current) return
    async function flush() {
      isSyncing.current = true
      try {
        const doFlushBody = async () => {
          const all = await db.offline_queue.toArray()
          const pending = all
            .filter(i => i.synced_at === null && !i.failed_at && (i.workspace_id ?? org!.id) === org!.id)
            .sort((a, b) => a.created_at.localeCompare(b.created_at))

          let abortedForAuth = false
          for (const item of pending) {
            try {
              if (item.procedure === 'orders.complete') {
                const orderInput = item.input as Exclude<Parameters<typeof trpcClient.orders.complete.mutate>[0], void>
                await trpcClient.orders.complete.mutate({ ...orderInput, force: true })
                if (orderInput.clientId) {
                  await db.sessions.delete(orderInput.clientId)
                  await db.session_lines.where('session_id').equals(orderInput.clientId).delete()
                }
              } else if (item.procedure === 'restock.complete') {
                await trpcClient.restock.complete.mutate(
                  item.input as Parameters<typeof trpcClient.restock.complete.mutate>[0]
                )
              } else if (item.procedure === 'adjustment.complete') {
                await trpcClient.adjustment.complete.mutate(
                  item.input as Parameters<typeof trpcClient.adjustment.complete.mutate>[0]
                )
              }
              // Only mark synced after the mutation succeeds — setting it first risks
              // permanently losing the item if the app is killed mid-flight.
              await db.offline_queue.update(item.id, { synced_at: new Date().toISOString() })
            } catch (err) {
              if (isTransportError(err)) {
                // Pure connectivity failure — don't count toward retry cap, retry next flush.
              } else if (isUnauthorizedError(err)) {
                // Token expired — all remaining items will fail the same way.
                // Abort the loop; items stay pending until the user re-authenticates.
                setAuthError(true)
                abortedForAuth = true
                break
              } else if (!isNetworkError(err)) {
                // Server definitively rejected (BAD_REQUEST/FORBIDDEN/etc.) — mark dead.
                // error_message is shown in OfflineFailedSheet, so store the
                // user-facing form, not the raw SDK string.
                const errMsg = err instanceof Error ? friendlyMutationError(err) : undefined
                await db.offline_queue.update(item.id, {
                  failed_at: new Date().toISOString(),
                  attempts: (item.attempts ?? 0) + 1,
                  ...(errMsg != null ? { error_message: errMsg } : {}),
                })
              } else {
                // Server error (5xx, rate-limit, etc.) — retryable but cap at 10 attempts.
                const newAttempts = (item.attempts ?? 0) + 1
                const errMsg = err instanceof Error ? friendlyMutationError(err) : undefined
                if (newAttempts >= 10) {
                  await db.offline_queue.update(item.id, {
                    failed_at: new Date().toISOString(),
                    attempts: newAttempts,
                    ...(errMsg != null ? { error_message: errMsg } : {}),
                  })
                } else {
                  await db.offline_queue.update(item.id, { attempts: newAttempts })
                }
              }
            }
          }

          if (!abortedForAuth) {
            setAuthError(false)
            if (pending.length > 0) {
              try {
                const products = await trpcClient.products.list.query({ archived: false })
                await db.products.bulkPut(products)
              } catch {}
              utils.products.list.invalidate()
              utils.dashboard.stats.invalidate()
              utils.dashboard.lowStock.invalidate()
              utils.dashboard.recentActivity.invalidate()
              utils.dashboard.oversold.invalidate()
              utils.credit.listDebtors.invalidate()
              utils.credit.getSummary.invalidate()
              utils.credit.getDebtorOrders.invalidate()
            }
          }

          try {
            const cursor = await db.meta.get(`history_synced_at:${org!.id}`)
            const historyData = await trpcClient.history.seed.query({ since: cursor?.value })
            await storeHistorySeed(historyData)
            await db.meta.put({ key: `history_synced_at:${org!.id}`, value: new Date().toISOString() })
          } catch {}
          utils.history.list.invalidate()

          try {
            const debtors = await trpcClient.credit.listDebtorNames.query()
            await db.credit_debtors.where('workspace_id').equals(org!.id).delete()
            await db.credit_debtors.bulkPut(debtors.map(d => ({ ...d, workspace_id: org!.id })))
          } catch {}

          // Prune synced queue items older than 7 days — they're on the server already.
          // Failed items are excluded; user handles those via the dead-letter UI.
          try {
            const cutoff = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
            await db.offline_queue
              .filter(i => i.synced_at !== null && i.synced_at < cutoff)
              .delete()
          } catch {}
        }

        // Web Locks API: prevents concurrent flushes across multiple open tabs.
        // ifAvailable: skip (not queue) if another tab already holds the lock.
        // Feature-detect for older WebViews that don't implement the Locks API.
        const hasWebLocks = typeof navigator !== 'undefined' && !!navigator.locks
        if (hasWebLocks) {
          await navigator.locks.request(
            'shelvi-offline-flush',
            { ifAvailable: true },
            async (lock) => {
              if (!lock) return
              await doFlushBody()
            }
          )
        } else {
          await doFlushBody()
        }
      } finally {
        isSyncing.current = false
      }
      await refreshCounts()
    }
    flush()
  }, [online, org?.id, flushTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic retry: re-attempt the flush every 60 s while online with pending items.
  // Handles partial flush failures on flaky connections without waiting for user action.
  useEffect(() => {
    if (!online || pendingCount === 0) return
    const timer = setInterval(() => setFlushTick(t => t + 1), 60_000)
    return () => clearInterval(timer)
  }, [online, pendingCount])

  useEffect(() => { refreshCounts() }, [])

  // Clear auth error whenever the session changes (user re-authenticated).
  useEffect(() => { setAuthError(false) }, [session?.user?.id])

  async function retryItem(id: string) {
    await db.offline_queue.update(id, { failed_at: null, attempts: 0, error_message: null })
    await refreshCounts()
    if (online) setFlushTick(t => t + 1)
  }

  async function discardItem(id: string) {
    const item = await db.offline_queue.get(id)
    if (item) {
      const clientId = (item.input as { clientId?: string }).clientId
      if (clientId) {
        await db.session_lines.where('session_id').equals(clientId).delete()
        await db.sessions.delete(clientId)
      }
    }
    await db.offline_queue.delete(id)
    await refreshCounts()
  }

  return (
    <OfflineContext.Provider value={{ online, pendingCount, failedCount, failedItems, authError, retryItem, discardItem }}>
      {children}
    </OfflineContext.Provider>
  )
}

export function useOffline() {
  return useContext(OfflineContext)
}

type SeedRow = Awaited<ReturnType<typeof trpcClient.history.seed.query>>[number]

async function storeHistorySeed(historyData: SeedRow[]) {
  const sessionsToStore: LocalSession[] = historyData.map(s => ({
    id: s.id,
    workspace_id: s.workspace_id,
    user_id: s.user_id,
    type: s.type,
    is_credit: s.is_credit,
    code: s.code,
    reason: s.reason ?? null,
    total: s.total,
    completed_at: s.completed_at,
    voided_at: s.voided_at,
    created_at: s.created_at,
  }))

  const linesToStore: LocalSessionLine[] = historyData.flatMap(s =>
    s.lines.map(l => ({
      id: l.id,
      session_id: l.session_id,
      product_id: l.product_id,
      qty: l.qty,
      price_snapshot: l.price_snapshot,
      cost_snapshot: l.cost_snapshot,
      product_name: l.product_name ?? undefined,
      product_code: l.product_code ?? undefined,
    }))
  )

  await db.sessions.bulkPut(sessionsToStore)
  await db.session_lines.bulkPut(linesToStore)
}
